const ZOOM_SCALE = 2.5;
const DESKTOP_MEDIA = '(min-width: 1024px)';
const SHARED_TOGGLE_ATTR = 'data-shared-zoom-toggle';

const clamp = (value) => Math.min(100, Math.max(0, value));

class GoatGalleryZoom extends HTMLElement {
  connectedCallback() {
    this.container = this.closest('.product-media-container');
    this.image = this.container?.querySelector('img.product-media__image');
    this.zoomSource = this.dataset.zoomSrc || this.image?.currentSrc || this.image?.src;

    if (!this.container || !this.image || !this.zoomSource) return;
    if (this.container.matches('.product-media-container--video, .product-media-container--model')) return;
    if (this.closest('dialog, quick-add-dialog, quick-add-component')) return;

    this.mediaQuery = window.matchMedia(DESKTOP_MEDIA);
    this.active = false;
    this.layer = null;
    this.toggle = null;
    this.slideshowSlides = this.container.closest('slideshow-slides');
    this.carouselViewport = this.slideshowSlides?.closest('slideshow-container');

    this.onClick = (event) => {
      if (!this.mediaQuery.matches) return;
      if (this.active) this.disengage();
      else this.engage(event.clientX, event.clientY);
    };
    this.onMouseMove = (event) => {
      if (this.active && this.mediaQuery.matches) this.moveTo(event.clientX, event.clientY);
    };
    this.onMouseLeave = () => {
      if (this.active && this.layer) this.layer.style.backgroundPosition = 'center';
    };
    this.onTouchMove = (event) => {
      if (!this.active) return;
      event.preventDefault();
      const touch = event.touches[0];
      if (touch) this.moveTo(touch.clientX, touch.clientY);
    };
    this.onToggleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.active) this.disengage();
      else this.engage();
    };
    this.onSharedToggleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const activeInstance = this.resolveActiveInstance();
      if (!activeInstance) return;
      if (activeInstance.active) {
        activeInstance.disengage();
        return;
      }
      // A slide change can leave another slide's layer engaged; clear it before zooming this one.
      this.eachInstance((instance) => {
        if (instance !== activeInstance && instance.active) instance.disengage();
      });
      activeInstance.engage();
    };
    this.onBreakpointChange = () => {
      this.disengage();
      this.teardownToggle();
      this.buildToggle();
    };

    this.container.addEventListener('click', this.onClick);
    this.container.addEventListener('mousemove', this.onMouseMove);
    this.container.addEventListener('mouseleave', this.onMouseLeave);
    this.container.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.mediaQuery.addEventListener('change', this.onBreakpointChange);

    this.container.classList.add('is-zoomable');
    this.buildToggle();
  }

  disconnectedCallback() {
    this.container?.removeEventListener('click', this.onClick);
    this.container?.removeEventListener('mousemove', this.onMouseMove);
    this.container?.removeEventListener('mouseleave', this.onMouseLeave);
    this.container?.removeEventListener('touchmove', this.onTouchMove);
    this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
    this.teardownToggle();
  }

  buildToggle() {
    const isDesktop = this.mediaQuery?.matches;
    const inCarousel = !!this.slideshowSlides && !!this.carouselViewport;

    if (!isDesktop && inCarousel) {
      this.buildSharedToggle();
    } else {
      this.buildLocalToggle();
    }
  }

  buildLocalToggle() {
    const button = this.createToggleButton();
    button.addEventListener('click', this.onToggleClick);
    this.toggle = button;
    this.container.appendChild(button);
  }

  buildSharedToggle() {
    const existing = this.carouselViewport.querySelector(`:scope > [${SHARED_TOGGLE_ATTR}]`);
    if (existing) {
      this.toggle = existing;
      return;
    }
    const button = this.createToggleButton();
    button.setAttribute(SHARED_TOGGLE_ATTR, '');
    button.addEventListener('click', this.onSharedToggleClick);
    this.toggle = button;
    this.carouselViewport.appendChild(button);
  }

  createToggleButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gallery-zoom-toggle';
    button.setAttribute('aria-label', this.dataset.toggleLabel || 'Toggle zoom');
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML =
      '<svg class="gallery-zoom-toggle__in" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M6 14H5v5h5v-1H6v-4Zm-1-4h1V6h4V5H5v5Zm13 8h-4v1h5v-5h-1v4ZM14 5v1h4v4h1V5h-5ZM15.08 12.558h-2.712v2.724h-.816v-2.724H8.828v-.816h2.724V9.018h.816v2.724h2.712v.816Z" fill="currentColor"/></svg>' +
      '<svg class="gallery-zoom-toggle__out" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" hidden>' +
      '<path d="M10.337 12.546v-.876h3.324v.876h-3.324ZM5 16h3v3h1v-4H5v1Zm3-8H5v1h4V5H8v3Zm7 11h1v-3h3v-1h-4v4Zm1-11V5h-1v4h4V8h-3Z" fill="currentColor"/></svg>';
    return button;
  }

  teardownToggle() {
    if (!this.toggle) return;
    const isShared = this.toggle.hasAttribute(SHARED_TOGGLE_ATTR);

    if (isShared && this.carouselViewport) {
      const siblings = this.carouselViewport.querySelectorAll('goat-gallery-zoom');
      const otherConnected = Array.from(siblings).some((el) => el !== this && el.toggle === this.toggle);
      if (!otherConnected) {
        this.toggle.removeEventListener('click', this.onSharedToggleClick);
        this.toggle.remove();
      }
    } else if (!isShared) {
      this.toggle.removeEventListener('click', this.onToggleClick);
      this.toggle.remove();
    }
    this.toggle = null;
  }

  eachInstance(fn) {
    if (!this.slideshowSlides) return fn(this);
    this.slideshowSlides.querySelectorAll('goat-gallery-zoom').forEach(fn);
  }

  // Measured rather than derived from scrollLeft/offsetWidth: slides can carry a gap or a
  // peeking neighbour, so index maths picks the wrong slide.
  resolveActiveInstance() {
    const slides = this.slideshowSlides;
    if (!slides) return this;
    const slideEls = slides.querySelectorAll(':scope > slideshow-slide');
    if (!slideEls.length) return this;

    const viewport = slides.getBoundingClientRect();
    if (!viewport.width) return this;
    const viewportCenter = viewport.left + viewport.width / 2;

    let closest = null;
    let closestDistance = Infinity;
    for (const slide of slideEls) {
      const rect = slide.getBoundingClientRect();
      if (!rect.width) continue;
      const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = slide;
      }
    }

    return closest?.querySelector('goat-gallery-zoom') || this;
  }

  ensureLayer() {
    if (this.layer) return this.layer;
    const layer = document.createElement('div');
    layer.className = 'gallery-zoom-layer';
    layer.style.backgroundImage = `url("${this.zoomSource}")`;
    layer.style.backgroundSize = `${ZOOM_SCALE * 100}%`;
    this.container.appendChild(layer);
    this.layer = layer;
    return layer;
  }

  moveTo(clientX, clientY) {
    if (!this.layer) return;
    const rect = this.container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.layer.style.backgroundPosition = `${clamp(((clientX - rect.left) / rect.width) * 100)}% ${clamp(
      ((clientY - rect.top) / rect.height) * 100
    )}%`;
  }

  engage(clientX, clientY) {
    const layer = this.ensureLayer();
    this.active = true;
    this.container.classList.add('is-zoomed');
    layer.classList.add('is-active');

    if (this.mediaQuery.matches && clientX !== undefined) {
      this.moveTo(clientX, clientY);
    } else {
      layer.style.backgroundPosition = 'center';
      this.setToggleState(true);
      this.lockCarousel(true);
    }
  }

  disengage() {
    if (!this.active) return;
    this.active = false;
    this.container.classList.remove('is-zoomed');
    this.layer?.classList.remove('is-active');
    this.setToggleState(false);
    this.lockCarousel(false);
  }

  setToggleState(zoomed) {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', String(zoomed));
    this.toggle.querySelector('.gallery-zoom-toggle__in')?.toggleAttribute('hidden', zoomed);
    this.toggle.querySelector('.gallery-zoom-toggle__out')?.toggleAttribute('hidden', !zoomed);
  }

  lockCarousel(locked) {
    const slides = this.container.closest('slideshow-slides');
    if (slides) slides.style.overflowX = locked ? 'hidden' : '';
  }
}

if (!customElements.get('goat-gallery-zoom')) {
  customElements.define('goat-gallery-zoom', GoatGalleryZoom);
}
